import type { ReactNode } from "react";
import { createContext, use } from "react";
import { useTranslation } from "@privacy/translations";
import { approveAssistantCommitMutationOptions, sendAssistantMessageMutationOptions } from "../../api/assistant";
import { useUserInfo } from "../../api/userInfo";
import { useCurrentOrganisation } from "../../root/CurrentOrganisationProvider";

export type AssistantApi = {
  sendMessageMutationOptions: () => ReturnType<typeof sendAssistantMessageMutationOptions>;
  approveCommitMutationOptions: () => ReturnType<typeof approveAssistantCommitMutationOptions>;
  firstMessage: string;
  displayName: string;
  defaultUnitName: string;
  isInvite: boolean;
};

export const AssistantApiContext = createContext<AssistantApi | null>(null);

export const useAssistantApi = () => {
  const context = use(AssistantApiContext);
  if (!context) throw new Error('"useAssistantApi" can only be used within an "AssistantApiContext"');

  return context;
};

export const OrganisationAssistantApi = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation();
  const user = useUserInfo();
  const organisation = useCurrentOrganisation();

  const value: AssistantApi = {
    sendMessageMutationOptions: sendAssistantMessageMutationOptions,
    approveCommitMutationOptions: approveAssistantCommitMutationOptions,
    firstMessage: t("Assistant.FirstMessage"),
    displayName: `${user.firstName} ${user.lastName}`.trim(),
    defaultUnitName: organisation.name,
    isInvite: false,
  };

  return <AssistantApiContext value={value}>{children}</AssistantApiContext>;
};
